'use client';

import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation'; // Added
import { useAuth } from '@/contexts/AuthContext'; // Added
import { useCart } from '@/contexts/CartContext';
import {
	X,
	Trash2,
	Plus,
	Minus,
	ShoppingBag,
	ArrowRight,
} from 'lucide-react';

// Helper: Currency Formatter
function formatCurrency(n: number) {
	return new Intl.NumberFormat('en-NG', {
		style: 'currency',
		currency: 'NGN',
		maximumFractionDigits: 0,
	}).format(n);
}

export default function CartSidebar() {
	const router = useRouter(); // Added hooks
	const { user } = useAuth(); // Added hooks
	const {
		open,
		setOpen,
		items,
		subtotal,
		setQty,
		removeItem,
		clear,
	} = useCart();

	// Prevent body scroll when cart is open
	useEffect(() => {
		if (open) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}
		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [open]);

	const close = () => setOpen(false);

	const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());
	const handleImgError = useCallback((id: string) => {
		setImgErrors((prev) => new Set(prev).add(id));
	}, []);

	// New Handler
	const handleCheckout = () => {
		close();
		if (!user) {
			router.push('/auth/login?redirect=/checkout');
		} else {
			router.push('/checkout');
		}
	};

	return (
		<>
			{/* Backdrop */}
			{open && (
				<div
					className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 opacity-100 pointer-events-auto"
					onClick={close}
					aria-hidden="true"
				/>
			)}

			{/* Sidebar Panel */}
			<aside
				className={`fixed top-0 right-0 h-full z-50 w-full sm:w-[450px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'
					}`}
			>
				{/* --- Header --- */}
				<div className="flex items-center justify-between p-5 border-b border-gray-100">
					<div className="flex items-center gap-2">
						<ShoppingBag className="w-5 h-5 text-[#051449]" />
						<h2 className="text-lg font-bold text-[#051449]">
							Your Cart ({items.length})
						</h2>
					</div>
					<button
						onClick={close}
						className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
						aria-label="Close cart"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				{/* --- Cart Items --- */}
				<div className="flex-1 overflow-y-auto p-5 space-y-6">
					{items.length === 0 ? (
						<div className="h-full flex flex-col items-center justify-center text-center">
							<div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
								<ShoppingBag className="w-10 h-10 text-gray-300" />
							</div>
							<h3 className="text-lg font-semibold text-gray-900">
								Your cart is empty
							</h3>
							<p className="text-gray-500 mt-2 max-w-xs text-sm">
								Looks like you haven't added anything to
								your cart yet.
							</p>
							<button
								onClick={() => {
									close();
									router.push('/products')
								}}
								className="mt-6 px-6 py-2.5 bg-[#FFCF23] text-[#051449] font-bold rounded-xl hover:bg-yellow-400 transition-colors"
							>
								Start Shopping
							</button>
						</div>
					) : (
						items.map((item) => (
							<div
								key={item.id}
								className="flex gap-4 group"
							>
								{/* Image */}
								<div className="relative w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
									{item.image && !imgErrors.has(item.id) ? (
										<Image
											src={item.image}
											alt={item.name}
											fill
											className="object-cover"
											sizes="80px"
											onError={() => handleImgError(item.id)}
										/>
									) : (
										<div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gray-50">
											<ShoppingBag className="w-5 h-5 text-gray-300" />
											<span className="text-[8px] text-gray-300 font-medium text-center leading-tight px-1">No Image</span>
										</div>
									)}
								</div>

								{/* Details */}
								<div className="flex-1 flex flex-col justify-between">
									<div>
										<div className="flex justify-between items-start gap-2">
											<h4 className="font-semibold text-gray-900 text-sm line-clamp-2 leading-snug">
												<Link
													href={`/products/${item.id}`}
													onClick={close}
												>
													{item.name}
												</Link>
											</h4>
											<button
												onClick={() => removeItem(item.id)}
												className="text-gray-400 hover:text-red-500 transition-colors"
												title="Remove item"
											>
												<Trash2 className="w-4 h-4" />
											</button>
										</div>
										{item.storeName && (
											<p className="text-xs text-gray-500 mt-1 truncate">
												Sold by {item.storeName}
											</p>
										)}
									</div>

									{/* Price & Qty */}
									<div className="flex items-end justify-between mt-2">
										<div className="text-sm font-bold text-[#051449]">
											{formatCurrency(item.price)}
										</div>

										<div className="flex items-center gap-3 bg-gray-50 rounded-lg p-1">
											<button
												onClick={() =>
													setQty(
														item.id,
														Math.max(1, item.qty - 1),
													)
												}
												className="w-6 h-6 flex items-center justify-center rounded bg-white shadow-sm text-gray-600 hover:text-black disabled:opacity-50"
												disabled={item.qty <= 1}
											>
												<Minus className="w-3 h-3" />
											</button>
											<span className="text-xs font-semibold w-4 text-center">
												{item.qty}
											</span>
											<button
												onClick={() =>
													setQty(item.id, item.qty + 1)
												}
												className="w-6 h-6 flex items-center justify-center rounded bg-white shadow-sm text-gray-600 hover:text-black"
											>
												<Plus className="w-3 h-3" />
											</button>
										</div>
									</div>
								</div>
							</div>
						))
					)}
				</div>

				{/* --- Footer --- */}
				{items.length > 0 && (
					<div className="p-5 border-t border-gray-100 bg-gray-50/50">
						<div className="space-y-3 mb-6">
							<div className="flex justify-between items-center text-gray-600 text-sm">
								<span>Subtotal</span>
								<span className="font-medium text-gray-900">
									{formatCurrency(subtotal)}
								</span>
							</div>
							<div className="flex justify-between items-center text-lg font-bold text-[#051449]">
								<span>Total</span>
								<span>{formatCurrency(subtotal)}</span>
							</div>
							<p className="text-xs text-gray-400 text-center">
								Shipping and taxes calculated at checkout.
							</p>
						</div>

						<div className="flex flex-col gap-3">
							<button
								onClick={handleCheckout}
								className="w-full py-3.5 bg-[#051449] text-white font-bold rounded-xl hover:bg-blue-900 transition-all shadow-lg flex items-center justify-center gap-2"
							>
								Checkout Now{' '}
								<ArrowRight className="w-4 h-4" />
							</button>

							<button
								onClick={() => clear()}
								className="w-full py-2.5 text-sm text-red-500 font-medium hover:bg-red-50 rounded-xl transition-colors"
							>
								Clear Cart
							</button>
						</div>
					</div>
				)}
			</aside>
		</>
	);
}
